import React, { useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiCheck, FiSearch } from 'react-icons/fi';

export interface DropdownOption { value: string; label: string }

interface Props {
    value: string;
    options: DropdownOption[];
    onChange: (value: string) => void;
    searchable?: boolean;
    disabled?: boolean;
}

// replaces a native <select> so the open list matches the v2 menus (client switcher look)
const Dropdown: React.FC<Props> = ({ value, options, onChange, searchable, disabled }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);
    const selectedRef = useRef<HTMLLIElement>(null);
    const selected = options.find(x => x.value === value);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        selectedRef.current?.scrollIntoView({ block: 'nearest' });
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const pick = (v: string) => {
        onChange(v);
        setOpen(false);
        setSearch('');
    };

    const q = search.trim().toLowerCase();
    const visible = q ? options.filter(x => x.label.toLowerCase().includes(q)) : options;

    return <div className={`v2-dropdown${open ? ' open' : ''}`} ref={rootRef}>
        <button type="button" className="dropdown-button" onClick={() => setOpen(!open)} disabled={disabled}>
            <span className="value">{selected?.label ?? ''}</span>
            <FiChevronDown className="caret" />
        </button>
        {open && <div className="dropdown-menu">
            {searchable && <div className="search"><FiSearch /><input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} autoFocus /></div>}
            <ul>
                {visible.map(x => <li key={x.value} ref={x.value === value ? selectedRef : undefined} className={x.value === value ? 'selected' : ''} onClick={() => pick(x.value)}>
                    <span className="label">{x.label}</span>
                    {x.value === value && <span className="tick"><FiCheck /></span>}
                </li>)}
                {visible.length === 0 && <li className="empty">No matches</li>}
            </ul>
        </div>}
    </div>;
};

export default Dropdown;
